import { EMPTY, formatDateFull } from './piFormat.js';
import { listOf, objectOf } from './piRequests.js';

export const DEMO_REASONS = [
  { value: 'demo', label: 'Demonstração nossa' },
  { value: 'internal', label: 'Interna ou teste nosso' },
  { value: 'other', label: 'Outro' },
];

export const OTHER_REASON = 'other';

export const SYNC_REASON = 'sync';

export const SYNC_REASON_LABEL = 'Marcada direto no cadastro';

export const DEMO_NOTE_LIMIT = 300;

export const DEMO_BANNER_TITLE = 'Empresa de demonstração — fora dos números do PI';

export const DEMO_BADGE_TITLE = 'Empresa de demonstração: fora de todos os números do PI, inclusive do histórico.';

export const ACTIVITY_HIDDEN_TEXT = 'Empresa fora dos números do PI: a atividade não é exibida.';

export const MARK_EFFECT_TEXT = 'Esta empresa sai de todos os números do PI (inclusive do histórico) e dos relatórios do Master. Os dados dela continuam no sistema; nada é apagado. O PI recalcula as janelas na próxima madrugada; até lá os totais podem mudar.';

export const UNMARK_EFFECT_TEXT = 'A empresa volta a todos os números do PI, inclusive no histórico, e aos relatórios do Master. O recálculo leva uma madrugada; até lá os totais podem mudar.';

export const MARK_DONE_TEXT = 'Empresa marcada como demonstração. O PI recalcula as janelas na próxima madrugada; até lá os totais podem mudar.';

export const UNMARK_DONE_TEXT = 'Empresa de volta aos números do PI. O recálculo leva uma madrugada; até lá os totais podem mudar.';

export const MARK_PENDING_TEXT = 'Marcado na empresa; o PI aplica na próxima madrugada.';

export const UNMARK_PENDING_TEXT = 'Desmarcado na empresa; o PI aplica na próxima madrugada.';

export const PORTUGAL_SHARD = 'EEEE';

export const OUTSIDE_PI = 'outside';

export const MARK_EFFECT_OUTSIDE_PI_TEXT = 'Esta empresa sai dos relatórios do Master. Empresas do tenant de Portugal não entram no PI, então os números do PI não mudam. Os dados dela continuam no sistema; nada é apagado.';

export const UNMARK_EFFECT_OUTSIDE_PI_TEXT = 'A empresa volta aos relatórios do Master. Empresas do tenant de Portugal não entram no PI, então os números do PI não mudam.';

export const MARK_OUTSIDE_PI_TEXT = 'Empresa marcada como demonstração e fora dos relatórios do Master. Empresas do tenant de Portugal não entram no PI.';

export const UNMARK_OUTSIDE_PI_TEXT = 'Empresa de volta aos relatórios do Master. Empresas do tenant de Portugal não entram no PI.';

export const REASON_REQUIRED_TEXT = 'Escolha o motivo da marcação.';

export const NOTE_REQUIRED_TEXT = 'Escreva a observação: ela é obrigatória quando o motivo é Outro.';

export const NOTE_TOO_LONG_TEXT = 'A observação passa de ' + DEMO_NOTE_LIMIT + ' caracteres.';

export const LEGACY_ROUTE_MISSING_TEXT = 'A marcação de demonstração ainda não está disponível no MarQ.API (404). Confira o deploy do legado.';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const statusOf = (error) => (error && error.response && typeof error.response.status === 'number' ? error.response.status : null);

const reasonEntry = (reason) => DEMO_REASONS.filter((entry) => entry.value === textOf(reason))[0] || null;

export const demoReasonLabel = (reason) => {
  const key = textOf(reason);
  if (!key) return '';
  if (key === SYNC_REASON) return SYNC_REASON_LABEL;
  const entry = reasonEntry(key);
  return entry ? entry.label : key;
};

export const blankDemoForm = (mode) => ({ mode: mode === 'unmark' ? 'unmark' : 'mark', reason: '', note: '' });

const cleanNote = (note) => textOf(note).replace(/\s+/g, ' ');

export const demoFormProblems = (form) => {
  const source = objectOf(form);
  const marking = source.mode !== 'unmark';
  const reason = textOf(source.reason);
  const note = cleanNote(source.note);
  const problems = { reason: '', note: '' };
  if (marking && !reasonEntry(reason)) problems.reason = REASON_REQUIRED_TEXT;
  if (note.length > DEMO_NOTE_LIMIT) problems.note = NOTE_TOO_LONG_TEXT;
  else if (marking && reason === OTHER_REASON && !note) problems.note = NOTE_REQUIRED_TEXT;
  return problems;
};

export const demoFormValid = (form) => {
  const problems = demoFormProblems(form);
  return !problems.reason && !problems.note;
};

const formValues = (form) => {
  const source = objectOf(form);
  const marking = source.mode !== 'unmark';
  const reason = marking && reasonEntry(source.reason) ? textOf(source.reason) : null;
  const note = cleanNote(source.note);
  return { marking: marking, reason: reason, note: note || null };
};

export const legacyDemoBody = (form) => {
  const values = formValues(form);
  return { hideFromReports: values.marking, reason: values.reason, note: values.note };
};

export const piDemoBody = (form, shardCode) => {
  const values = formValues(form);
  return { shard: textOf(shardCode).toUpperCase(), isDemo: values.marking, reason: values.reason, note: values.note };
};

const boolOrNull = (value) => (value === true || value === false ? value : null);

export const legacyDemoState = (body) => {
  const source = objectOf(body);
  const change = source.lastChange && typeof source.lastChange === 'object' ? source.lastChange : null;
  return {
    companyId: textOf(source.companyId),
    hideFromReports: boolOrNull(source.hideFromReports),
    deleted: source.deleted === true,
    lastChange: change
      ? {
        at: textOf(change.at),
        userName: textOf(change.userName),
        hideFromReports: boolOrNull(change.hideFromReports),
        reason: textOf(change.reason),
        note: textOf(change.note),
      }
      : null,
  };
};

const changeFor = (legacy, pi, isDemo) => {
  const change = legacy ? legacy.lastChange : null;
  if (change && (change.hideFromReports === null || change.hideFromReports === isDemo)) {
    return { since: change.at, reason: change.reason, by: change.userName, note: change.note };
  }
  const piSource = objectOf(pi);
  if (isDemo && piSource.isDemo === true && piSource.demo && typeof piSource.demo === 'object') {
    return { since: textOf(piSource.demo.since), reason: textOf(piSource.demo.reason), by: textOf(piSource.demo.byName), note: textOf(piSource.demo.note) };
  }
  return null;
};

const sinceText = (value) => {
  const text = formatDateFull(value);
  return text === EMPTY ? '' : text;
};

export const demoDetails = (change) => {
  const source = objectOf(change);
  const parts = [];
  const since = sinceText(source.since);
  const reason = demoReasonLabel(source.reason);
  if (since) parts.push('desde ' + since);
  if (reason) parts.push('Motivo: ' + reason);
  if (textOf(source.by)) parts.push('por ' + textOf(source.by));
  return parts;
};

export const demoView = (legacy, pi) => {
  const known = !!legacy && legacy.hideFromReports !== null;
  const piSource = pi && typeof pi === 'object' ? pi : null;
  const piKnown = !!piSource && (piSource.isDemo === true || piSource.isDemo === false);
  const isDemo = known ? legacy.hideFromReports : (piKnown && piSource.isDemo === true);
  const change = changeFor(known ? legacy : null, piSource, isDemo);
  const details = isDemo ? demoDetails(change) : [];
  let pendingText = '';
  if (known && piKnown && piSource.isDemo !== legacy.hideFromReports) pendingText = legacy.hideFromReports ? MARK_PENDING_TEXT : UNMARK_PENDING_TEXT;
  let lastText = '';
  if (!isDemo && change) {
    const since = sinceText(change.since);
    lastText = since ? 'Desmarcada em ' + since + (textOf(change.by) ? ' por ' + textOf(change.by) : '') + '.' : '';
  }
  const deleted = known && legacy.deleted === true;
  return {
    known: known,
    deleted: deleted,
    editable: known && !deleted,
    isDemo: isDemo,
    details: details,
    detailText: details.join(' · '),
    note: isDemo && change ? textOf(change.note) : '',
    pendingText: pendingText,
    lastText: lastText,
  };
};

export const demoRowText = (row) => {
  const source = objectOf(row);
  if (source.isDemo !== true) return '';
  const parts = [];
  const reason = demoReasonLabel(source.demoReason);
  const since = sinceText(source.demoSince);
  if (reason) parts.push(reason);
  if (since) parts.push('desde ' + since);
  if (textOf(source.demoByName)) parts.push('por ' + textOf(source.demoByName));
  return parts.join(' · ');
};

export const legacyErrorText = (error, action) => {
  const data = error && error.response ? error.response.data : null;
  const errors = data && typeof data === 'object' && Array.isArray(data.erros) ? data.erros : null;
  const messages = listOf(errors)
    .map((item) => textOf(item && typeof item === 'object' ? (item.value || item.message) : item))
    .filter((message) => !!message);
  if (messages.length) return messages.join(' ');
  const status = statusOf(error);
  if (status === 401 || status === 403) return 'Sem permissão para ' + action + ' (' + status + '). É necessário um usuário operador da plataforma.';
  if (status === 404 && !errors) return LEGACY_ROUTE_MISSING_TEXT;
  return 'Não foi possível ' + action + (status ? ' (' + status + ')' : '') + '.';
};

export const piCountsShard = (shardCode) => textOf(shardCode).toUpperCase() !== PORTUGAL_SHARD;

export const demoEffectText = (marking, shardCode) => {
  if (!piCountsShard(shardCode)) return marking ? MARK_EFFECT_OUTSIDE_PI_TEXT : UNMARK_EFFECT_OUTSIDE_PI_TEXT;
  return marking ? MARK_EFFECT_TEXT : UNMARK_EFFECT_TEXT;
};

export const demoDoneText = (marking, piApplied) => {
  if (piApplied === OUTSIDE_PI) return marking ? MARK_OUTSIDE_PI_TEXT : UNMARK_OUTSIDE_PI_TEXT;
  if (piApplied) return marking ? MARK_DONE_TEXT : UNMARK_DONE_TEXT;
  return marking ? MARK_PENDING_TEXT : UNMARK_PENDING_TEXT;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piCompanyDemoModel.js